import { PowerSettingsNew } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CrudPage,
  CrudTableAction,
  CrudTableActionType,
  DataGridColumnDefinition,
  DateLabel,
  EnabledStatusLabel,
} from 'components';
import { FilterDefinitionType } from 'enums';
import { useProviderTypes, useQueryFilters, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, ProviderType } from 'types';
import { filtersUtils, formatUtils } from 'utils';

import { ProviderTypeDialog } from './ProviderTypeDialog';

type ProviderTypeFilters = {
  code?: string;
  name?: string;
  isActive?: 'true' | 'false';
  isSystem?: 'true' | 'false';
  from?: string;
  to?: string;
};

const KEYS = {
  title: 'pages.provider_types.title',
  fields: {
    code: 'pages.provider_types.fields.code',
    name: 'pages.provider_types.fields.name',
    description: 'pages.provider_types.fields.description',
    is_active: 'pages.provider_types.fields.is_active',
    is_system: 'pages.provider_types.fields.is_system',
    created_at: 'pages.provider_types.fields.created_at',
    updated_at: 'pages.provider_types.fields.updated_at',
  },
} as const;

type DialogState = {
  open: boolean;
  data?: ProviderType;
};

type Props = {
  onCreateRef?: React.MutableRefObject<(() => void) | null>;
};

export const ProviderTypesPage: React.FC<Props> = ({ onCreateRef }) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const { isAdmin, isTechOperator } = useUser();
  const { filters } = useQueryFilters<ProviderTypeFilters>();
  const { queryResult, removeMutation, activateMutation, deactivateMutation } =
    useProviderTypes();

  const [dialog, setDialog] = useState<DialogState>({ open: false });

  const canManage = useMemo(
    () => isAdmin || isTechOperator,
    [isAdmin, isTechOperator],
  );

  const handleCreate = useCallback(() => {
    setDialog({ open: true });
  }, []);

  useEffect(() => {
    if (onCreateRef) {
      onCreateRef.current = handleCreate;
    }
  }, [onCreateRef, handleCreate]);

  const handleEdit = useCallback((item: ProviderType) => {
    setDialog({ open: true, data: item });
  }, []);

  const handleClose = useCallback(() => {
    setDialog({ open: false });
  }, []);

  const filtersDefinitions: FilterDefinition<ProviderTypeFilters>[] = useMemo(
    () => [
      {
        name: 'code',
        label: t(KEYS.fields.code),
        type: FilterDefinitionType.Text,
      },
      {
        name: 'name',
        label: t(KEYS.fields.name),
        type: FilterDefinitionType.Text,
      },
      {
        name: 'isActive',
        label: t(KEYS.fields.is_active),
        type: FilterDefinitionType.Select,
        options: [
          { value: 'true', label: tCommon('common.enabled') as string },
          { value: 'false', label: tCommon('common.disabled') as string },
        ],
      },
      {
        name: 'isSystem',
        label: t(KEYS.fields.is_system),
        type: FilterDefinitionType.Select,
        options: [
          { value: 'true', label: tCommon('common.yes') as string },
          { value: 'false', label: tCommon('common.no') as string },
        ],
      },
      ...filtersUtils.getDataRangeFilters(
        filters,
        FilterDefinitionType.DateRange,
        t(KEYS.fields.created_at),
      ),
    ],
    [t, tCommon, filters],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<ProviderType>[] => [
      {
        header: t(KEYS.fields.code),
        valueGetter: (item) => item.code,
        valueClassName: 'tw-font-medium',
      },
      {
        header: t(KEYS.fields.name),
        valueGetter: (item) => item.name,
      },
      {
        header: t(KEYS.fields.is_active),
        valueGetter: (item) => (
          <EnabledStatusLabel status={item.isActive ? 'active' : 'inactive'} />
        ),
      },
      {
        header: t(KEYS.fields.is_system),
        valueGetter: (item) =>
          tCommon(item.isSystem ? 'common.yes' : 'common.no'),
      },
      {
        header: t(KEYS.fields.created_at),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
        ),
      },
    ],
    [t, tCommon],
  );

  const handleToggleActive = useCallback(
    (item: ProviderType) => {
      if (item.isActive) {
        deactivateMutation.mutate(item.id);
      } else {
        activateMutation.mutate(item.id);
      }
    },
    [activateMutation, deactivateMutation],
  );

  const actions = useMemo((): CrudTableAction[] => {
    const result: CrudTableAction[] = [
      {
        type: CrudTableActionType.Details,
        onClick: handleEdit,
      },
    ];

    if (canManage) {
      result.push({
        type: CrudTableActionType.Custom,
        render: (item: ProviderType) => (
          <IconButton
            title={formatUtils.formatEnabled(!item.isActive)}
            color={item.isActive ? 'warning' : 'success'}
            onClick={() => handleToggleActive(item)}
          >
            <PowerSettingsNew />
          </IconButton>
        ),
      });
      result.push({
        type: CrudTableActionType.Remove,
        onRemove: (item: ProviderType, { close }: { close: () => void }) => {
          removeMutation.mutate(item.id, { onSuccess: close });
        },
      });
    }

    return result;
  }, [canManage, handleEdit, handleToggleActive, removeMutation]);

  return (
    <>
      <CrudPage
        header={{
          divider: false,
          ...(!onCreateRef &&
            canManage && {
              rightContentButton: { onClick: handleCreate },
            }),
        }}
        filters={{ filtersDefinitions }}
        table={{
          queryResult,
          columns,
          paginated: true,
          actions,
        }}
      />

      <ProviderTypeDialog
        open={dialog.open}
        data={dialog.data}
        onClose={handleClose}
      />
    </>
  );
};
